/**
 * useCoRespondersForInstance — Resolve co-responders for a workflow instance.
 *
 * DRR-126 Phase 3: Co-responder visibility. Given a constellation instance + workflow
 * instance ID, returns all actors who have tasks in that instance grouped by actor,
 * with their aggregate task status (pending/in_progress/completed/overdue).
 *
 * This enables Ring 1 actor nodes in constellation instance mode to show peer awareness:
 * "CNA-001 is IN_PROGRESS on this admission. Janitor-003 is ASSIGNED."
 *
 * Data is T2 (operational, role-scoped). No clinical data exposed across roles.
 * Backend enforces visibility per DRR-102 + DRR-114.
 */

import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const CO_RESPONDERS_QUERY = gql`
  query CoRespondersForInstance($constellationId: String!, $instanceId: String!, $currentActorId: String) {
    coRespondersForInstance(constellationId: $constellationId, instanceId: $instanceId, currentActorId: $currentActorId) {
      constellationId
      instanceId
      coResponders {
        actorId
        taskId
        taskName
        taskType
        status
        isSelf
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CoResponder {
  actorId: string;
  taskId: string;
  taskName: string;
  taskType: string;
  /** "pending" | "in_progress" | "completed" | "overdue" */
  status: string;
  /** True if this is the current actor (self-reference for UI highlight) */
  isSelf: boolean;
}

interface QueryData {
  coRespondersForInstance: {
    constellationId: string;
    instanceId: string;
    coResponders: CoResponder[];
  };
}

export interface CoRespondersResult {
  coResponders: CoResponder[];
  /** Peers only — excludes the current actor (isSelf === false) */
  peers: CoResponder[];
  loading: boolean;
  error?: Error;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetch co-responders for a specific workflow instance.
 *
 * @param constellationId - Constellation instance ID
 * @param instanceId - Workflow instance ID (Ring 3 node instanceId)
 * @param currentActorId - Current actor for self-marking (optional)
 * @param skip - Skip query (e.g. when no instance is selected)
 */
export function useCoRespondersForInstance(
  constellationId: string | undefined,
  instanceId: string | undefined,
  currentActorId?: string,
  skip = false,
): CoRespondersResult {
  const { data, loading, error } = useQuery<QueryData>(CO_RESPONDERS_QUERY, {
    variables: {
      constellationId: constellationId ?? '',
      instanceId: instanceId ?? '',
      currentActorId: currentActorId ?? null,
    },
    skip: skip || !constellationId || !instanceId,
    fetchPolicy: 'cache-and-network',
  });

  const coResponders = data?.coRespondersForInstance?.coResponders ?? [];

  const peers = useMemo(
    () => coResponders.filter((r) => !r.isSelf),
    [coResponders],
  );

  return {
    coResponders,
    peers,
    loading,
    error: error as Error | undefined,
  };
}
